import { LocalizationContextBuilder } from '@amzn/arb-tools';
import { MbmProvider } from '@amzn/react-arb-tools';
import React, { ReactNode, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { AppInitializationFailed } from '../components/common/ErrorFallback';
import { DEFAULT_LOCALE_EN_US, TRANSLATION_ROUTE, eErrorMessages } from '../constants/generic-constants';
import { useEmbedParams } from '../hooks/useEmbedParams';
import arbManifest from '../i18n/translations/arbManifest';
import { store } from '../store/store';
import { FlashMessageProvider } from './AppBannerContext';
import AppInitializer from './AppInitializer';
import { AppRouter } from './AppRouter';
import { AuthContextProvider } from './auth/AuthContextProvider';
import { getDocumentTitle } from 'src/utils/document-title-utils';

const localizationContext = new LocalizationContextBuilder().withLocale(DEFAULT_LOCALE_EN_US).withDefaultLocale(DEFAULT_LOCALE_EN_US).build();

const mbmOptions = {
  arbManifest,
  defaultLocalizationContext: localizationContext,
  urlPrefix: TRANSLATION_ROUTE
};

interface AppProps {
  children?: ReactNode;
}

export const App: React.FC<AppProps> = ({ children }) => {
  const handleErrorReset = () => {
    window.location.reload();
  };

  const queryClient = new QueryClient();
  const { isEmbedded } = useEmbedParams();

  useEffect(() => {
    document.title = getDocumentTitle();
  }, []);

  // Apply embedded-mode class to body when embedded
  useEffect(() => {
    if (isEmbedded) {
      document.body.classList.add('embedded-mode');
    } else {
      document.body.classList.remove('embedded-mode');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('embedded-mode');
    };
  }, [isEmbedded]);

  return (
    <ErrorBoundary
      FallbackComponent={({ error, resetErrorBoundary }) =>
        AppInitializationFailed(error, resetErrorBoundary, eErrorMessages.APP_INITIALIZATION_FAILED)
      }
      onReset={handleErrorReset}
    >
      <MbmProvider {...mbmOptions}>
        <FlashMessageProvider>
          <AuthContextProvider>
            <QueryClientProvider client={queryClient}>
              <Provider store={store}>
                <AppInitializer>
                  <AppRouter />
                </AppInitializer>
              </Provider>
            </QueryClientProvider>
          </AuthContextProvider>
        </FlashMessageProvider>
      </MbmProvider>
    </ErrorBoundary>
  );
};
